import { track } from '@/utils/tracking';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@mui/material';
import {
  useDeleteOperatorsFilesMutation,
  usePostOperatorsFilesMutation,
  usePostOperatorsVehiclesFilesPhotosRankMutation,
} from 'api/queries/operators';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import Cookies from 'js-cookie';
import differenceWith from 'lodash-es/differenceWith';
import isEqual from 'lodash-es/isEqual';
import { UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ControlledField, DroppableFileInputField, FileTypeEnum } from 'remote/global';
import { CookieKeyEnum } from 'utils/data/enums';
import { hasReadOnlyPermission, runSequentially } from 'utils/helpers';
import { VehicleImageSchemaType, vehicleImageSchema } from './types';

export function VehicleImageFormFields({ form }: { form: UseFormReturn<VehicleImageSchemaType> }) {
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const {
    control,
    formState: { isSubmitting, isValidating },
  } = form;
  const { permissions } = userInfo ?? {};
  const isFormDisabled =
    isSubmitting || isValidating || hasReadOnlyPermission(permissions?.VEHICLE_FILE ?? '');
  const jwtToken = Cookies.get(CookieKeyEnum.JWT_TOKEN) as string;

  const handleRejectFile = (alertMessage: string) => toast.error(alertMessage);

  return (
    <ControlledField
      control={control}
      name='attachments'
      disabled={isFormDisabled}
      render={(renderProps) => (
        <DroppableFileInputField
          {...renderProps}
          translationKey='vehicleImages'
          type={FileTypeEnum.VEHICLE_PHOTO}
          jwtToken={jwtToken}
          onFileRejected={handleRejectFile}
        />
      )}
    />
  );
}

function VehicleImageForm({
  defaultValues,
  vehicleId,
}: {
  defaultValues?: VehicleImageSchemaType;
  vehicleId: string;
}) {
  const { t } = useTranslation();
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo ?? {};
  const formInstance = useForm<VehicleImageSchemaType>({
    defaultValues: {
      attachments: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(vehicleImageSchema),
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;
  const deleteOperatorsFilesMutation = useDeleteOperatorsFilesMutation();
  const postOperatorsFilesMutation = usePostOperatorsFilesMutation();
  const postOperatorsVehiclesFilesPhotosRankMutation =
    usePostOperatorsVehiclesFilesPhotosRankMutation(vehicleId);

  const onValid = async (data: VehicleImageSchemaType) => {
    const files = data.attachments ?? [];
    // A file is uploaded if there is 'originalFile' attached
    const uploadedFiles = files.filter((f) => f.originalFile != null);

    const defaultFiles = defaultValues?.attachments ?? [];
    const removedFiles = differenceWith(defaultFiles, files, isEqual);

    try {
      const removedPromises = removedFiles.map(
        ({ fileName }) =>
          () =>
            deleteOperatorsFilesMutation.mutateAsync({ fileName })
      );
      const insertedPromises = uploadedFiles.map((file) => () => {
        const formData = new FormData();
        formData.append('file', file.originalFile as File);
        formData.append('fileType', file.fileType);
        formData.append('referenceId', vehicleId);
        return postOperatorsFilesMutation.mutateAsync(formData);
      });

      // Call the promise functions sequentially
      await runSequentially(removedPromises);
      await runSequentially(insertedPromises, (d) => track.partner_file_upload(d[0].fileType));

      const orderChanged = defaultFiles.some((file, index) => !isEqual(file, files[index]));
      if (orderChanged && files.length > 0) {
        await postOperatorsVehiclesFilesPhotosRankMutation.mutateAsync(files);
      }
    } catch (err) {
      toast.error(t('Toast.failure'));
    }
  };

  const isFormDisabled =
    isLoading ||
    isSubmitting ||
    isValidating ||
    hasReadOnlyPermission(permissions?.VEHICLE_FILE ?? '');

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <VehicleImageFormFields form={formInstance} />
      <Button
        type='submit'
        variant='contained'
        disabled={isFormDisabled}
        fullWidth
        sx={{ mt: '2rem' }}
      >
        {t('save')}
      </Button>
    </form>
  );
}

export default VehicleImageForm;
